import type { FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';

import { hasVendorDeviceSelection } from '../../../components/vendorAndDeviceRadioList';
import type { VendorDeviceSelection } from '../../../components/vendorAndDeviceRadioList';
import { type VendorDeviceMeta, buildVendorDeviceMetaFormPatch, readVendorDeviceMeta } from '../../../components/vendorDevice';
import { copyOptionMetaBetweenForms } from '../../../platform/option-meta';
import { hasMeaningfulValue, isPlainObject } from '../../../utils';
import { isCpuConfigured, isMemoryConfigured } from '../domain/cpu';
import { resolveVendorDevicePath } from '../domain/paths';
import { flattenToFormPatch } from '../shared/formFieldUtils';
import type { ComputeModalResourceKind, ComputePaths } from '../types';
import { MODAL_PATHS } from './modalPaths';

interface ComputeModalBridgeOptions {
  memoryMin?: number;
  cpuMin?: number;
  /** 容器直建：弹窗内仅配置 cpu.request 单字段 */
  cpuSimpleMode?: boolean;
}

/** 兼容 Arco 嵌套 path 与扁平 'a.b' key */
function readModalField(values: Record<string, unknown>, path: string): unknown {
  const nested = get(values, path);
  if (nested !== undefined) return nested;
  return values[path];
}

function clampToMin(value: unknown, min: number): number | undefined {
  if (value == null || value === '') return undefined;
  const num = Number(value);
  if (!Number.isFinite(num)) return undefined;
  return Math.max(num, min);
}

function readCpuFromParent(parentValues: Record<string, unknown>, parentPaths: ComputePaths) {
  return {
    physicalCards: get(parentValues, parentPaths.cpuCards),
    physicalCores: get(parentValues, parentPaths.cpuCores),
    logicalCores: get(parentValues, parentPaths.cpuLogical),
  };
}

/** 读取主表单厂商设备；兼容旧路径 resource.cpu.pricing */
function readParentVendorDeviceMeta(
  parentValues: Record<string, unknown>,
  parentPaths: ComputePaths,
  kind: ComputeModalResourceKind,
): VendorDeviceMeta | undefined {
  const primary = resolveVendorDevicePath(kind, parentPaths);
  const fromPrimary = readVendorDeviceMeta(parentValues, primary);
  if (fromPrimary) return fromPrimary;

  if (kind === 'cpu' && primary.endsWith('cpuPricing')) {
    return readVendorDeviceMeta(parentValues, primary.replace(/cpuPricing$/, 'cpu.pricing'));
  }
  return undefined;
}

function buildModalVendorDevicePatch(parentValues: Record<string, unknown>, parentPaths: ComputePaths, kind: ComputeModalResourceKind) {
  const meta = readParentVendorDeviceMeta(parentValues, parentPaths, kind);
  return buildVendorDeviceMetaFormPatch(resolveVendorDevicePath(kind, MODAL_PATHS), meta);
}

/** 从主表单读取当前资源片段，注入弹窗独立 Form */
export function buildModalFormValues(
  parentValues: Record<string, unknown>,
  parentPaths: ComputePaths,
  kind: ComputeModalResourceKind,
  options?: ComputeModalBridgeOptions,
): Record<string, unknown> {
  if (kind === 'cpu') {
    if (options?.cpuSimpleMode && parentPaths.cpuRequest) {
      const cpuMin = options.cpuMin ?? 1;
      const rawCpu = isCpuConfigured(parentValues, parentPaths) ? get(parentValues, parentPaths.cpuRequest) : cpuMin;
      return {
        [MODAL_PATHS.cpuRequest!]: clampToMin(rawCpu, cpuMin),
        ...buildModalVendorDevicePatch(parentValues, parentPaths, kind),
      };
    }
    const cpu = isCpuConfigured(parentValues, parentPaths)
      ? readCpuFromParent(parentValues, parentPaths)
      : { physicalCards: 1, physicalCores: 1, logicalCores: 1 };
    return {
      [MODAL_PATHS.cpuCards]: cpu.physicalCards,
      [MODAL_PATHS.cpuCores]: cpu.physicalCores,
      [MODAL_PATHS.cpuLogical]: cpu.logicalCores,
      ...buildModalVendorDevicePatch(parentValues, parentPaths, kind),
    };
  }

  if (kind === 'memory') {
    const memoryMin = options?.memoryMin ?? 1;
    const rawMemory = isMemoryConfigured(parentValues, parentPaths) ? get(parentValues, parentPaths.memory) : memoryMin;
    return {
      [MODAL_PATHS.memory]: clampToMin(rawMemory, memoryMin),
      ...buildModalVendorDevicePatch(parentValues, parentPaths, kind),
    };
  }

  const rawGpu = get(parentValues, parentPaths.gpu);
  const gpuRecord = isPlainObject(rawGpu) ? ({ ...rawGpu } as Record<string, unknown>) : {};
  const patch: Record<string, unknown> = {
    /** useWatch('gpu') / 计价单选需要前缀对象；叶子字段供 PersistFields 回显 */
    [MODAL_PATHS.gpu]: gpuRecord,
    ...flattenToFormPatch(MODAL_PATHS.gpu, gpuRecord),
  };
  if (hasVendorDeviceSelection(gpuRecord as VendorDeviceSelection)) {
    const rawNumber = get(parentValues, `${parentPaths.gpu}.number`) ?? gpuRecord.number;
    patch[`${MODAL_PATHS.gpu}.number`] = hasMeaningfulValue(rawNumber) ? Number(rawNumber) : 1;
    if (isPlainObject(patch[MODAL_PATHS.gpu])) {
      (patch[MODAL_PATHS.gpu] as Record<string, unknown>).number = patch[`${MODAL_PATHS.gpu}.number`];
    }
  }
  return patch;
}

/** 弹窗确认后写回主表单（按叶子 field 路径写入，确保 useWatch / innerForm 可收集） */
export function applyModalFormValues(
  parentForm: FormInstance,
  parentPaths: ComputePaths,
  kind: ComputeModalResourceKind,
  modalValues: Record<string, unknown>,
  options?: ComputeModalBridgeOptions,
) {
  let patch: Record<string, unknown> = {};
  const modalVendorPath = resolveVendorDevicePath(kind, MODAL_PATHS);
  const parentVendorPath = resolveVendorDevicePath(kind, parentPaths);

  if (kind === 'cpu') {
    if (options?.cpuSimpleMode && parentPaths.cpuRequest && MODAL_PATHS.cpuRequest) {
      patch = {
        [parentPaths.cpuRequest]: readModalField(modalValues, MODAL_PATHS.cpuRequest),
        [parentPaths.cpuCards]: undefined,
        [parentPaths.cpuCores]: undefined,
        [parentPaths.cpuLogical]: undefined,
      };
    } else {
      patch = {
        [parentPaths.cpuCards]: readModalField(modalValues, MODAL_PATHS.cpuCards),
        [parentPaths.cpuCores]: readModalField(modalValues, MODAL_PATHS.cpuCores),
        [parentPaths.cpuLogical]: readModalField(modalValues, MODAL_PATHS.cpuLogical),
      };
    }
  } else if (kind === 'memory') {
    patch = { [parentPaths.memory]: readModalField(modalValues, MODAL_PATHS.memory) };
  } else {
    const gpu = readModalField(modalValues, MODAL_PATHS.gpu);
    if (gpu !== undefined) {
      Object.assign(patch, { [parentPaths.gpu]: gpu }, flattenToFormPatch(parentPaths.gpu, gpu));
    }
  }

  /** 仅在弹窗确有厂商设备选中时写回；读不到时不覆盖主表单已有选择 */
  if (kind !== 'gpu') {
    const vendorMeta = readVendorDeviceMeta(modalValues, modalVendorPath);
    if (vendorMeta && hasVendorDeviceSelection(vendorMeta)) {
      Object.assign(patch, buildVendorDeviceMetaFormPatch(parentVendorPath, vendorMeta));
    }
  }

  parentForm.setFieldsValue(patch);
  if (kind !== 'gpu') {
    copyOptionMetaBetweenForms(parentForm, modalValues, [modalVendorPath]);
  } else {
    copyOptionMetaBetweenForms(parentForm, modalValues, [MODAL_PATHS.gpu]);
  }
}
